import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound } from "lucide-react";
import { getToken, authFetch } from "@/adminAuth";

const API = `/api`;

const input =
  "w-full bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-3 text-sm text-[#FAF8F5] transition-colors";

export default function AdminAccount() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!getToken()) navigate("/admin/login");
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (next !== confirm) {
      setError("Les deux nouveaux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setMessage("Mot de passe modifié. Il sera demandé à la prochaine connexion.");
        setCurrent("");
        setNext("");
        setConfirm("");
      } else {
        setError(typeof data.detail === "string" ? data.detail : "Modification impossible");
      }
    } catch {
      setError("Erreur de connexion au serveur");
    }
    setLoading(false);
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="admin-account-page">
      <div className="max-w-xl mx-auto px-6 sm:px-12">
        <Link
          to="/admin"
          data-testid="admin-account-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Les fiches
        </Link>

        <div className="mt-8 flex items-center gap-3">
          <KeyRound className="w-5 h-5 text-gold" />
          <h1 className="font-serif font-light text-3xl">Mot de passe</h1>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-7" data-testid="admin-password-form">
          <div>
            <label htmlFor="pwd-current" className="label-accent block mb-3">Mot de passe actuel</label>
            <input
              id="pwd-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={input}
              data-testid="pwd-current-input"
            />
          </div>
          <div>
            <label htmlFor="pwd-new" className="label-accent block mb-3">Nouveau mot de passe (8 caractères min.)</label>
            <input
              id="pwd-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={input}
              data-testid="pwd-new-input"
            />
          </div>
          <div>
            <label htmlFor="pwd-confirm" className="label-accent block mb-3">Confirmer le nouveau mot de passe</label>
            <input
              id="pwd-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={input}
              data-testid="pwd-confirm-input"
            />
          </div>

          {error && <p className="text-xs text-red-400" data-testid="pwd-error">{error}</p>}
          {message && <p className="text-sm text-gold" data-testid="pwd-success">{message}</p>}

          <button
            type="submit"
            disabled={loading}
            data-testid="pwd-submit-button"
            className="bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            {loading ? "Modification…" : "Modifier le mot de passe"}
          </button>
        </form>
      </div>
    </div>
  );
}
